import { ReactElement, useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import * as S from './styles'

export const EMPTY_RESULT_MESSAGE = 'Nenhuma venda encontrada para os filtros selecionados'

export function KpiSkeleton(): ReactElement {
  return (
    <S.Row aria-busy="true" aria-label="Carregando indicadores">
      {[0, 1, 2].map((index) => (
        <S.SkeletonCard key={index} $height="125.6px">
          <Skeleton width="45%" height={12} />
          <Skeleton width="60%" height={30} style={{ marginTop: 12 }} />
          <Skeleton width="75%" height={12} style={{ marginTop: 10 }} />
        </S.SkeletonCard>
      ))}
    </S.Row>
  )
}

export function GroupsSkeleton({ groups = 2, label = 'Carregando vendas' }: { groups?: number, label?: string }): ReactElement {
  return (
    <S.Stack aria-busy="true" aria-label={label}>
      {Array.from({ length: groups }, (_, index) => (
        <S.SkeletonGroup key={index}>
          <Skeleton height={22} width="40%" />
          <Skeleton count={4} height={36} style={{ marginTop: 12 }} />
        </S.SkeletonGroup>
      ))}
    </S.Stack>
  )
}

export function EmptyState({ message = EMPTY_RESULT_MESSAGE }: { message?: string }): ReactElement {
  return (
    <S.Message role="status">
      <S.MessageText>{message}</S.MessageText>
    </S.Message>
  )
}

interface ErrorStateProps {
  message?: string
  onRetry: () => void
}

export function ErrorState({ message = 'Não foi possível carregar o relatório.', onRetry }: ErrorStateProps): ReactElement {
  return (
    <S.Message role="alert">
      <S.MessageText>{message}</S.MessageText>
      <S.RetryButton type="button" onClick={onRetry}>
        Tentar novamente
      </S.RetryButton>
    </S.Message>
  )
}

interface TruncatedNoticeProps {
  rowCount: number
  totalRowCount: number
  onExport?: () => void
  exportDisabled?: boolean
}

export function TruncatedNotice({ rowCount, totalRowCount, onExport, exportDisabled = false }: TruncatedNoticeProps): ReactElement {
  return (
    <S.Notice role="status">
      <span>
        {`Mostrando ${rowCount.toLocaleString('pt-BR')} de ${totalRowCount.toLocaleString('pt-BR')} linhas. `}
        Exporte o relatório para ver todas.
      </span>
      {onExport && (
        <S.NoticeAction type="button" onClick={onExport} disabled={exportDisabled} aria-busy={exportDisabled || undefined}>
          Exportar XLSX
        </S.NoticeAction>
      )}
    </S.Notice>
  )
}

export function CapacityAlert({ message }: { message: string }): ReactElement {
  return (
    <S.Notice role="status" data-tone="alert">
      <span>{message}</span>
    </S.Notice>
  )
}

export const REPORT_LOADING_MESSAGES = [
  'Aquecendo os motores…',
  'Apurando os números…',
  'Contabilizando as receitas…',
  'Separando os ingressos…',
  'Conferindo os lotes…',
  'Calculando o estoque…',
  'Montando o relatório…',
]

const BD_MARK = `${process.env.PUBLIC_URL || ''}/favicon.svg`
const MESSAGE_INTERVAL_MS = 1400

export function ReportLoading({ label = 'Carregando relatório' }: { label?: string }): ReactElement {
  const [index, setIndex] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setIndex((current) => (current + 1) % REPORT_LOADING_MESSAGES.length), MESSAGE_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [])
  return (
    <S.LoadBox role="status" aria-label={label}>
      <S.HiddenText>{label}</S.HiddenText>
      <S.SpinLogo src={BD_MARK} alt="" />
      <S.LoadMsg aria-hidden="true">{REPORT_LOADING_MESSAGES[index]}</S.LoadMsg>
    </S.LoadBox>
  )
}
